import type { ResourceType } from "@medplum/fhirtypes";
// This file allows us to inject logging into functions declared in the FhirResource
// base model. It's done this way because the base model is used across both web and mobile, which use different logging methods.
// This works by registering a global logger, and using that logger (if registered) in failures in FhirResource.ts

// For now, this just logs errors for failed writes.
// But this can and should be extended for additional logging for medplum actions (ie Failed Reads).
// Models that override or do not use the functions declared in FhirResource will not get this utility.

export interface FhirWriteFailureDetails {
  resourceType: ResourceType;
  /** Medplum client operation, e.g. `createResource`, `updateResource`. */
  operation: string;
  error: unknown;
  isSyncFrom: string[] | undefined;
}
export type FHIRWriteFailureReporter = (details: FhirWriteFailureDetails) => void;
let fhirWriteFailureReporter: FHIRWriteFailureReporter | undefined;

/**
 * Registers a global handler for failed FHIR writes on model classes.
 */
export function registerFhirWriteFailureReporter(reporter: FHIRWriteFailureReporter): void {
  if (fhirWriteFailureReporter) {
    return;
  }
  fhirWriteFailureReporter = reporter;
}

/** Called in FhirResource to handle write errors. */
export function reportFHIRWriteFailureIfApplicable(details: FhirWriteFailureDetails): void {
  if (!fhirWriteFailureReporter) {
    return;
  }

  try {
    fhirWriteFailureReporter(details);
  } catch {
    // Logging failures should never disrupt actual operations.
    // This will silently fail since no logger is available at this level, which is unfortunate, but still
    // preferred to the alternative.
  }
}

// ---------------------------------------------------------------------------
// Practice-account (compartment) self-heal reporting
// ---------------------------------------------------------------------------
// When a create reaches the write path without a resolved practice account, the base
// model tries to derive one from the resource (see `ensurePracticeAccountForCreate` in
// FhirResource). We report every fallback so leaky callers — which should pass
// `options.practiceOrganization` explicitly — can be found and fixed. Registered by the
// host app; a no-op until then (e.g. on mobile).

export interface AccountCompartmentFallbackDetails {
  resourceType: ResourceType;
  /** `self-healed` — an account was derived and applied; `unresolved` — none could be derived. */
  outcome: "self-healed" | "unresolved";
  /** The derived Organization reference, when `outcome === "self-healed"`. */
  practiceReference?: string;
}
export type AccountCompartmentFallbackReporter = (
  details: AccountCompartmentFallbackDetails,
) => void;
let accountCompartmentFallbackReporter: AccountCompartmentFallbackReporter | undefined;

/**
 * Registers a global handler for practice-account self-heal events on model creates.
 */
export function registerAccountCompartmentFallbackReporter(
  reporter: AccountCompartmentFallbackReporter,
): void {
  if (accountCompartmentFallbackReporter) {
    return;
  }
  accountCompartmentFallbackReporter = reporter;
}

/** Called in FhirResource when a create's practice account is derived or cannot be resolved. */
export function reportAccountCompartmentFallback(details: AccountCompartmentFallbackDetails): void {
  if (!accountCompartmentFallbackReporter) {
    return;
  }

  try {
    accountCompartmentFallbackReporter(details);
  } catch {
    // Telemetry must never disrupt a write.
  }
}
